import React, { FC, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { Box, MenuItem } from '@material-ui/core'
import { setInitialReference } from 'actions/timeline'
import { showReferenceFileChoiceAction } from 'actions/mainView'
import { useAction } from 'hooks'
import Button from 'components/base/Button'
import Select from 'components/base/Select'
import { TranslationContext } from 'contexts/TranslationContext'
import { selectReferenceFilesData } from 'selectors/mainView'
import { DEFAULT_FPS } from 'Util'
import { ReactComponent as CloseIcon } from '~/assets/settings/navigation_close.svg'
import './ReferenceFileChoice.scss'

interface IProps {
  visible: boolean
}

type referenceFileType = {
  id: string,
  width: number,
  height: number,
  fpsNum: number,
  fpsDenum: number
  dfFlag: number,
  filetype: string | null,
}

export const ReferenceFileChoiceComponent: FC<IProps> = ({ visible }) => {
  const [ value, setValue ] = useState<referenceFileType>({} as referenceFileType)
  const refFiles = useSelector(selectReferenceFilesData)

  const onCloseWindow = useAction(showReferenceFileChoiceAction)
  const onSelectReferenceFile = useAction(setInitialReference)
  const { t } = useContext(TranslationContext)
  const onClose = (apply = false) => onCloseWindow({ visibility: false, apply })
  const onChangeReference = (e: MouseEvent) => {
    const target = e.target as HTMLInputElement
    const file = refFiles.find(file => file.id === target.value)
    if (file) {
      const { id, width, height, fpsNum, fpsDenum } = file
      setValue({
        id,
        width,
        height,
        fpsNum: fpsNum || DEFAULT_FPS,
        fpsDenum: fpsDenum || 1,
        dfFlag: file.dfFlag,
        filetype: file.filetype,
      })
    }
  }

  const ok = () => {
    if (!isEmpty(value)) {
      onSelectReferenceFile(value)
    }
    setValue({} as referenceFileType)
    onClose(true)
  }

  useEffect(() => {
    setValue(prev => {
      if (isEmpty(prev) && refFiles?.[0]) {
        const { id, width, height, fpsNum, fpsDenum, dfFlag, filetype } = refFiles[0]
        return {
          id,
          width,
          height,
          fpsNum: fpsNum || DEFAULT_FPS,
          fpsDenum: fpsDenum || 1,
          dfFlag,
          filetype,
        }
      }
      return prev
    })
  }, [ refFiles ])

  if (!visible) return null
  return (
    <Box className="ref-file-choice" height="100%">
      <div className="ref-file-choice__close" onClick={() => onClose(false)}>
        <CloseIcon />
      </div>
      <div className="ref-file-choice__body allow-draggable">
        <div className="h1 allow-draggable">{t('MODAL_WINDOW_REFERENCE_FILE_CHOICE_TITLE')}</div>
        <div className="ref-file-choice__text">{t('MODAL_WINDOW_REFERENCE_FILE_CHOICE_DESCRIPTION')}</div>
        <Box width="100%" display="flex" justifyContent="center" alignItems="center">
          <Select
            value={value?.id || ''}
            onChange={onChangeReference}
            className="ref-file-choice__select"
            rowHeight={33}
            maxHeight={100}
          >
            {refFiles.map(file => (
              <MenuItem
                className="ref-file-choice__menu-item menu-item"
                key={`ref-file-choice-menu_item_${file.name}`}
                value={file.id}
              >
                <Box display="flex">
                  <div className="menu-item__name">{file.name}</div>
                  <span>
                    {`${file.width}x${file.height}  |  ${(file.fps).toFixed(2)} FPS |`}
                  </span>
                  &nbsp;
                  <span className="menu-item__codec">{`${file.codecsInfo}`}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      </div>
      <Box display="flex" justifyContent="center" className="ref-file-choice__footer" gridGap="16px">
        <Button
          type="submit"
          className="ref-file-choice__cancel btn--border"
          primary
          onClick={() => onClose(false)}
          autoFocus
        >
          {t('CONFIRMATION_DIALOG_BTN_CANCEL')}
        </Button>
        <Button
          type="submit"
          className="ref-file-choice__ok"
          primary
          onClick={ok}
          autoFocus
        >
          {t('SAVE_SETTINGS_BTN_OK')}
        </Button>
      </Box>
    </Box>
  )
}
